import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Sofa, Users, Calendar, MessageCircle, Crown, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CouchSurfing = () => {
  const { user, token } = useContext(AuthContext);
  const [hosts, setHosts] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedHost, setSelectedHost] = useState(null);
  const [requestDialog, setRequestDialog] = useState(false);
  const [requestData, setRequestData] = useState({
    check_in_date: '',
    check_out_date: '',
    num_guests: 1,
    message: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.is_premium) {
      fetchHosts();
      fetchReceivedRequests();
      fetchSentRequests();
    }
  }, []);

  const fetchHosts = async () => {
    setLoading(true);
    try {
      const params = locationFilter ? `?location=${locationFilter}` : '';
      const response = await axios.get(`${API}/hosting/available-hosts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHosts(response.data);
    } catch (error) {
      toast.error('Failed to load hosts');
    } finally {
      setLoading(false);
    }
  };

  const fetchReceivedRequests = async () => {
    try {
      const response = await axios.get(`${API}/hosting/requests/received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceivedRequests(response.data);
    } catch (error) {
      console.error('Failed to load received requests');
    }
  };

  const fetchSentRequests = async () => {
    try {
      const response = await axios.get(`${API}/hosting/requests/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSentRequests(response.data);
    } catch (error) {
      console.error('Failed to load sent requests');
    }
  };

  const sendHostingRequest = async () => {
    if (!requestData.check_in_date || !requestData.check_out_date || !requestData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(
        `${API}/hosting/request`,
        { ...requestData, host_id: selectedHost.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hosting request sent!');
      setRequestDialog(false);
      setRequestData({
        check_in_date: '',
        check_out_date: '',
        num_guests: 1,
        message: ''
      });
      fetchSentRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const respondToRequest = async (requestId, status) => {
    try {
      await axios.put(
        `${API}/hosting/request/${requestId}/respond`,
        { request_id: requestId, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Request ${status}!`);
      fetchReceivedRequests();
    } catch (error) {
      toast.error('Failed to respond to request');
    }
  };

  // If not premium, show upgrade prompt
  if (!user?.is_premium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8 pt-24">
          <div className="glass-dark rounded-3xl p-12 text-center">
            <Sofa className="w-20 h-20 text-gray-400 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-4">Couch Surfing</h1>
            <p className="text-gray-600 mb-6 text-lg">
              Connect with hosts around the world and find a place to stay!
            </p>
            <div className="bg-gray-100 border-2 border-black rounded-2xl p-6 mb-6">
              <Crown className="w-12 h-12 text-black mx-auto mb-3" />
              <p className="text-lg font-semibold mb-2">Premium Feature</p>
              <p className="text-gray-600">
                Upgrade to Premium to access Couch Surfing and connect with hosts worldwide.
              </p>
            </div>
            <Button
              onClick={() => navigate('/subscription')}
              className="bg-black hover:bg-gray-800 text-white rounded-xl px-8 py-6 text-lg font-semibold"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Premium
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Sofa className="w-10 h-10 text-teal" />
            <h1 className="text-4xl font-bold">Couch Surfing</h1>
            <Badge className="bg-gold text-white">Premium</Badge>
          </div>
          <p className="text-gray-600 text-lg">Find hosts or manage your hosting requests</p>
        </div>

        <Tabs defaultValue="find" className="space-y-6">
          <TabsList className="glass-dark p-1 rounded-2xl">
            <TabsTrigger value="find" className="rounded-xl data-[state=active]:bg-teal data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Find Hosts
            </TabsTrigger>
            <TabsTrigger value="received" className="rounded-xl data-[state=active]:bg-teal data-[state=active]:text-white">
              <MessageCircle className="w-4 h-4 mr-2" />
              Received ({receivedRequests.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="rounded-xl data-[state=active]:bg-teal data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Sent ({sentRequests.length})
            </TabsTrigger>
          </TabsList>

          {/* Find Hosts Tab */}
          <TabsContent value="find" className="space-y-6">
            <div className="glass-dark rounded-2xl p-4">
              <div className="flex space-x-2">
                <Input
                  placeholder="Search by location..."
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchHosts()}
                  className="rounded-xl"
                />
                <Button
                  onClick={fetchHosts}
                  className="bg-teal text-white rounded-xl"
                >
                  Search
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal mx-auto"></div>
              </div>
            ) : hosts.length === 0 ? (
              <div className="glass-dark rounded-3xl p-12 text-center">
                <Sofa className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No hosts found. Try adjusting your search.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hosts.map((host) => (
                  <div key={host.id} className="glass-dark rounded-3xl p-6 relative">
                    <Badge className="absolute top-4 right-4 bg-teal text-white">
                      <Sofa className="w-3 h-3 mr-1" />
                      Host
                    </Badge>

                    <div className="flex flex-col items-center text-center mb-4">
                      <Avatar className="w-20 h-20 mb-3 border-4 border-teal">
                        <AvatarImage src={host.profile_photo} />
                        <AvatarFallback className="bg-white text-black text-xl font-bold">
                          {host.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <h3 className="text-xl font-bold">{host.name}</h3>
                      {host.location?.city && (
                        <p className="text-gray-600 text-sm">{host.location.city}, {host.location.country}</p>
                      )}
                    </div>

                    {host.hosting_details && (
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm">
                          <CheckCircle className="w-4 h-4 text-teal mr-2" />
                          <span className="capitalize">{host.hosting_details.space_type?.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Users className="w-4 h-4 text-teal mr-2" />
                          <span>Up to {host.hosting_details.max_guests} guests</span>
                        </div>
                        {host.hosting_details.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">{host.hosting_details.description}</p>
                        )}
                      </div>
                    )}

                    <div className="flex space-x-2">
                      <Button
                        onClick={() => navigate(`/user/${host.id}`)}
                        variant="outline"
                        className="flex-1 rounded-xl border-2 border-teal"
                      >
                        View Profile
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedHost(host);
                          setRequestDialog(true);
                        }}
                        className="flex-1 bg-teal text-white rounded-xl"
                      >
                        Request Stay
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Received Requests Tab */}
          <TabsContent value="received" className="space-y-4">
            {receivedRequests.length === 0 ? (
              <div className="glass-dark rounded-3xl p-12 text-center">
                <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No hosting requests received yet</p>
              </div>
            ) : (
              receivedRequests.map((request) => (
                <div key={request.id} className="glass-dark rounded-2xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-12 h-12 border-2 border-teal">
                        <AvatarFallback className="bg-white text-black font-bold">
                          {request.requester_name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-bold">{request.requester_name}</h3>
                        <p className="text-sm text-gray-600">
                          {request.num_guests} {request.num_guests === 1 ? 'guest' : 'guests'}
                        </p>
                      </div>
                    </div>
                    <Badge className={
                      request.status === 'accepted' ? 'bg-green-500' :
                      request.status === 'declined' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }>
                      {request.status}
                    </Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 text-teal mr-2" />
                      <span>{request.check_in_date} → {request.check_out_date}</span>
                    </div>
                    <p className="text-gray-700">{request.message}</p>
                  </div>

                  {request.status === 'pending' && (
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => respondToRequest(request.id, 'accepted')}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-xl"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept
                      </Button>
                      <Button
                        onClick={() => respondToRequest(request.id, 'declined')}
                        variant="outline"
                        className="flex-1 border-2 border-red-500 text-red-500 hover:bg-red-50 rounded-xl"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Decline
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </TabsContent>

          {/* Sent Requests Tab */}
          <TabsContent value="sent" className="space-y-4">
            {sentRequests.length === 0 ? (
              <div className="glass-dark rounded-3xl p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">You haven't sent any hosting requests yet</p>
              </div>
            ) : (
              sentRequests.map((request) => (
                <div key={request.id} className="glass-dark rounded-2xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-12 h-12 border-2 border-teal">
                        <AvatarFallback className="bg-white text-black font-bold">
                          {request.host_name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-bold">{request.host_name}</h3>
                        <p className="text-sm text-gray-600">Host</p>
                      </div>
                    </div>
                    <Badge className={
                      request.status === 'accepted' ? 'bg-green-500' :
                      request.status === 'declined' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }>
                      {request.status}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 text-teal mr-2" />
                      <span>{request.check_in_date} → {request.check_out_date}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Users className="w-4 h-4 text-teal mr-2" />
                      <span>{request.num_guests} {request.num_guests === 1 ? 'guest' : 'guests'}</span>
                    </div>
                    <p className="text-gray-700 text-sm">{request.message}</p>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Request Dialog */}
      <Dialog open={requestDialog} onOpenChange={setRequestDialog}>
        <DialogContent className="max-w-md bg-white rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Request to Stay</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedHost && (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                <Avatar className="w-12 h-12 border-2 border-teal">
                  <AvatarImage src={selectedHost.profile_photo} />
                  <AvatarFallback className="bg-white text-black font-bold">
                    {selectedHost.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-bold">{selectedHost.name}</p>
                  <p className="text-sm text-gray-600">{selectedHost.location?.city}</p>
                </div>
              </div>
            )}

            <div>
              <Label>Check-in Date</Label>
              <Input
                type="date"
                value={requestData.check_in_date}
                onChange={(e) => setRequestData({ ...requestData, check_in_date: e.target.value })}
                className="rounded-xl"
                required
              />
            </div>

            <div>
              <Label>Check-out Date</Label>
              <Input
                type="date"
                value={requestData.check_out_date}
                onChange={(e) => setRequestData({ ...requestData, check_out_date: e.target.value })}
                className="rounded-xl"
                required
              />
            </div>

            <div>
              <Label>Number of Guests</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={requestData.num_guests}
                onChange={(e) => setRequestData({ ...requestData, num_guests: parseInt(e.target.value) || 1 })}
                className="rounded-xl"
              />
            </div>

            <div>
              <Label>Message to Host</Label>
              <Textarea
                value={requestData.message}
                onChange={(e) => setRequestData({ ...requestData, message: e.target.value })}
                placeholder="Tell the host about yourself and why you'd like to stay..."
                className="rounded-xl"
                rows={4}
                required
              />
            </div>

            <Button
              onClick={sendHostingRequest}
              className="w-full bg-gradient-to-r from-teal to-teal-light text-white rounded-xl py-6 text-lg font-semibold"
            >
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouchSurfing;
